<template>
  <v-container>
    <!-- FILTERS + ACTIONS -->
    <v-card class="mb-3">
      <v-toolbar
        flat
        dense
        :color="
          $vuetify.theme.dark ? 'blue-grey darken-4' : 'blue-grey lighten-5'
        "
      >
        <v-toolbar-title class="text-subtitle-2"
        >
          Sales Receipts
        </v-toolbar-title
        >
        <v-spacer />
        <v-btn text :loading="saleInvLoading" @click="getData">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
        <v-btn
          text
          :loading="printing"
          :disabled="printing"
          @click="printReceipts"
        >
          <v-icon>mdi-download</v-icon>
        </v-btn>
        <div>
          <export-sale-inventory />
        </div>
      </v-toolbar>

      <v-card-text class="py-2">
        <v-row dense>
          <v-col cols="12" sm="4" md="3">
            <v-text-field
              v-model="dateStart"
              label="Date start"
              type="date"
              hide-details="auto"
              clearable
              dense
              prepend-inner-icon="mdi-calendar-start"
            />
          </v-col>

          <v-col cols="12" sm="4" md="3">
            <v-text-field
              v-model="dateEnd"
              label="Date end"
              type="date"
              hide-details="auto"
              clearable
              dense
              prepend-inner-icon="mdi-calendar-end"
            />
          </v-col>

          <v-col cols="12" sm="4" md="3">
            <v-text-field
              v-model="search"
              :loading="saleInvLoading"
              append-icon="mdi-magnify"
              label="Search receipt / product / notes"
              hide-details="auto"
              clearable
              dense
            />
          </v-col>

          <v-col cols="12" md="3" class="d-flex align-center">
            <div class="d-flex flex-wrap gap-8">
              <v-chip small label @click="presetRange('today')">
                Today
              </v-chip>
              <v-chip small label @click="presetRange('7d')"
              >
                Last 7 days
              </v-chip
              >
              <v-chip small label @click="presetRange('30d')"
              >
                Last 30 days
              </v-chip
              >
              <v-chip
                small
                label
                color="red"
                text-color="white"
                @click="presetRange('clear')"
              >
                Clear
              </v-chip
              >
            </div>
          </v-col>
        </v-row>

        <!-- KPIs -->
        <v-row dense class="mt-1">
          <v-col cols="12" sm="4">
            <v-sheet class="kpi" rounded>
              <div class="kpi-label">
                Receipts
              </div>
              <div class="kpi-value">
                {{ saleInvList.length || 0 }}
              </div>
            </v-sheet>
          </v-col>
          <v-col cols="12" sm="4">
            <v-sheet class="kpi" rounded>
              <div class="kpi-label">
                Gross Amount
              </div>
              <div class="kpi-value">
                {{ totalGross.phpFormat() }}
              </div>
            </v-sheet>
          </v-col>
          <v-col cols="12" sm="4">
            <v-sheet class="kpi" rounded>
              <div class="kpi-label">
                Total Discount
              </div>
              <div class="kpi-value">
                {{ totalDiscount.phpFormat() }}
              </div>
            </v-sheet>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- TABLE -->
    <div style="overflow: auto">
      <v-responsive>
        <v-data-table
          class="elevation-0 sales-table"
          dense
          :headers="headers"
          :items="saleInvList"
          :items-per-page.sync="itemsPerPage"
          :page.sync="page"
          :server-items-length="saleInvCount"
          :footer-props="{ 'items-per-page-options': [5, 10, 15, 20] }"
          :loading="saleInvLoading"
          item-key="receipt_no"
          show-expand
          single-expand
        >
          <!-- Loading -->
          <template v-slot:loading>
            <v-skeleton-loader type="table" class="mx-2 my-2" />
          </template>

          <!-- Row -->
          <template v-slot:item="{ item, expand, isExpanded }">
            <tr>
              <td class="border-right text-right" width="120">
                {{ `${item.receipt_no}` }}
              </td>
              <td class="border-right text-right" width="100">
                {{ item.receipts.length }}
              </td>
              <td class="border-right text-right">
                {{ (item.discount || 0).phpFormat() }}
              </td>
              <td class="border-right text-right">
                {{ (item.amount || 0).phpFormat() }}
              </td>
              <td class="border-right text-right">
                {{ (item.date_sold || '').dateFormat() }}
              </td>
              <td class="text-right" style="white-space: nowrap">
                <v-tooltip v-if="authPermissions.has('sales:void')" bottom>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      v-bind="attrs"
                      color="purple"
                      text
                      x-small
                      v-on="on"
                      @click="voidItem(item.receipt_no)"
                    >
                      <v-icon small>
                        mdi-eraser-variant
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>Void receipt</span>
                </v-tooltip>

                <!-- PDF -->
                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      v-bind="attrs"
                      color="blue"
                      text
                      x-small
                      v-on="on"
                      @click="downloadReceipt(item)"
                    >
                      <v-icon small>
                        mdi-file-pdf-box
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>Download PDF</span>
                </v-tooltip>

                <!-- Expand -->
                <v-btn
                  text
                  x-small
                  :class="isExpanded ? 'is-expanded' : ''"
                  @click="expand(!isExpanded)"
                >
                  <v-icon small>
                    {{ isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                  </v-icon>
                </v-btn>
              </td>
            </tr>
          </template>

          <!-- Expanded -->
          <template v-slot:expanded-item="{ item }">
            <td :colspan="headers.length" class="pa-0">
              <v-simple-table dense class="subtable">
                <thead>
                  <tr>
                    <th class="py-1">
                      Product
                    </th>
                    <th class="py-1">
                      Brand
                    </th>
                    <th class="py-1 text-right">
                      Total Disc
                    </th>
                    <th class="py-1 text-right">
                      SRP
                    </th>
                    <th class="py-1 text-right">
                      Qty
                    </th>
                    <th class="py-1 text-right">
                      Amount
                    </th>
                    <th class="py-1 text-right">
                      Returned
                    </th>
                    <th class="py-1">
                      Remarks
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(subItem, index) in item.receipts" :key="index">
                    <td class="border-right">
                      <v-btn
                        v-if="authPermissions.has('sales:edit')"
                        color="yellow darken-3"
                        text
                        x-small
                        class="mr-1"
                        @click="editSaleInventory(subItem)"
                      >
                        <v-icon small>
                          mdi-pencil
                        </v-icon>
                      </v-btn>
                      <span class="ellipsis">{{ subItem.product_name }}</span>
                    </td>
                    <td class="border-right ellipsis">
                      {{ subItem.brand_name }}
                    </td>
                    <td class="border-right text-right">
                      {{ (subItem.discount || 0).phpFormat() }}
                    </td>
                    <td class="border-right text-right">
                      {{ (subItem.retail || 0).phpFormat() }}
                    </td>
                    <td class="border-right text-right">
                      {{ subItem.quantity }}
                    </td>
                    <td class="text-right">
                      {{ (subItem.total_amount || 0).phpFormat() }}
                    </td>
                    <td class="text-right" style="white-space: nowrap">
                      <span v-if="subItem.returned_quantity != null" class="teal--text text-caption">
                        {{ subItem.returned_quantity }} {{ subItem.unit }}
                        ({{ ((subItem.returned_retail || 0) * subItem.returned_quantity).phpFormat() }})
                      </span>
                      <return-product-btn
                        v-if="authPermissions.has('sales:edit')"
                        :sale-inventory-id="subItem.id"
                        :returned-product-id="subItem.returned_product_id"
                        @click="onReturnProduct"
                      />
                    </td>
                    <td class="remarks">
                      <v-chip
                        v-if="subItem.promotion_code"
                        x-small
                        label
                        class="mr-1"
                      >
                        {{ subItem.promotion_code }}
                      </v-chip>
                      <span v-if="subItem.promotion_code === 'cross_freebie'">
                        {{ subItem.promo_stock_code }} •
                        {{ subItem.rule_config.buy_qty }} for
                        {{ subItem.rule_config.free_qty }}
                      </span>
                      <span
                        v-else-if="
                          subItem.promotion_code &&
                            !['b1g1', 'b1gx', 'x4y'].includes(
                              subItem.promotion_code,
                            )
                        "
                      >
                        {{ subItem.rule_config.buy_qty }} for
                        {{ subItem.rule_config.get_qty }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </td>
          </template>

          <!-- Page total -->
          <template v-slot:body.append>
            <tr class="table-total">
              <td :colspan="headers.length - 2" class="text-right pr-2">
                <strong>Page Totals</strong>
              </td>
              <td class="text-right">
                <strong>{{ pageAmount.phpFormat() }}</strong>
              </td>
              <td />
            </tr>
          </template>

          <!-- Empty -->
          <template v-slot:no-data>
            <div class="pa-6 text-center">
              <v-icon x-large class="mb-2">
                mdi-receipt-text
              </v-icon>
              <div class="mb-1 font-weight-medium">
                No sales found
              </div>
              <div class="text-caption grey--text">
                Try adjusting dates or clearing the search
              </div>
            </div>
          </template>
        </v-data-table>
      </v-responsive>
    </div>

    <return-product-partial-create-dialog ref="returnDialog" />
  </v-container>
</template>

<style lang="scss" scoped>
.v-btn.is-expanded .v-icon {
  transform: rotate(180deg);
}

.sales-table ::v-deep thead th {
  position: sticky;
  top: 0;
  z-index: 2;
}

.border-right {
  border-right: 1px solid rgba(128, 128, 128, 0.2);
}

.subtable {
  width: 100%;
}

.subtable thead th {
  background-color: rgba(0, 0, 0, 0.03);
}

.table-total td {
  background-color: rgba(0, 0, 0, 0.04);
}

.kpi {
  padding: 0.6rem 0.8rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.kpi-label {
  font-size: 0.75rem;
  opacity: 0.75;
}

.kpi-value {
  font-weight: 700;
}

.ellipsis {
  max-width: 260px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.remarks {
  max-width: 320px;
}

.gap-8 > * {
  margin-right: 0.5rem;
  margin-bottom: 0.25rem;
}
</style>

<script>
import { mapState } from 'vuex'
import SaleReceiptPDF from '@/pdf/sale-receipt.pdf'
import { moneyMixin } from './../../../mixins/helper.mixins'
import saleInvRepository from '@/repositories/sale-inventory.repository'
import { apiVars } from '@/extra-functions/api-helper.extra'
import { errorHandler } from '@/extra-functions/store.extra'
import searchDelay from '@/extra-functions/searchDelay.extra'
import {
  ConfirmPopup,
  ErrorPopup,
  LoadingPopup,
  SuccessPopup,
} from '@/lib/utils'
import appAxios from '@/extra-functions/axios.extra'
import PrintReceiptsV2 from '@/pdf/print-receipts-v2.pdf'

const searchTimeout = searchDelay()
const navigationDelay = searchDelay()

function tableVars() {
  return {
    searching: false,
    headers: [
      { text: 'Receipt No', value: 'receipt_no' },
      { text: 'Items', value: 'receipts.length' },
      { text: 'Total Disc', value: 'discount' },
      { text: 'Total Amount', value: 'amount' },
      { text: 'Date Sold', value: 'date_sold' },
      { text: '', sortable: false, value: 'data-table-expand' },
    ],
    itemsPerPage: 5,
    page: 1,
    search: null,
    dateStart: null,
    dateEnd: null,
  }
}

export default {
  name: 'VIndexRecievedInventories',
  components: {
    ExportSaleInventory: () => import('./ExportXLS.vue'),
    ReturnProductBtn: () => import('@/components/features/ReturnProductBtn.vue'),
    ReturnProductPartialCreateDialog: () => import('@/components/features/ReturnProductPartialCreateDialog.vue'),
  },
  props: {
    defaultConsumer: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      ...tableVars(),
      ...apiVars('saleInv', { count: 0, counting: false }),
    }
  },
  created() {
    this.getData()
  },
  computed: {
    ...mapState({
      saleInventoryUpdated: (state) => state.saleInventory.channelUpdate,
      storeId: (state) => state.settings.storeId,
      authPermissions: (state) => state.auth.userPermissions,
      token: (state) => state.auth.token,
    }),
    // Totals (client-side for the displayed items)
    pageAmount() {
      return (this.saleInvList || []).reduce(
        (a, b) => a + (Number(b.amount) || 0),
        0,
      )
    },
    totalGross() {
      return (this.saleInvList || []).reduce(
        (a, b) => a + (Number(b.amount) || 0),
        0,
      )
    },
    totalDiscount() {
      return (this.saleInvList || []).reduce(
        (a, b) => a + (Number(b.discount) || 0),
        0,
      )
    },
  },
  watch: {
    itemsPerPage() {
      this.saleInvLoading = true
      navigationDelay(() => {
        this.getSaleInv()
      })
    },
    page() {
      this.saleInvLoading = true
      navigationDelay(() => {
        this.getSaleInv()
      })
    },
    search() {
      this.getData()
    },
    dateStart() {
      this.getData()
    },
    dateEnd() {
      this.getData()
    },
    saleInventoryUpdated() {
      this.getData()
    },
  },
  methods: {
    async downloadReceipt(item) {
      try {
        LoadingPopup.fire('Generating receipt PDF...')

        // console.log({ ...item })
        const { data } = await appAxios(this.token).get(
          '/v2/sale-inventories/get-receipt',
          {
            params: {
              receipt_no: item.receipt_no,
            },
          },
        )

        if (!data?.data) {
          throw new Error('Receipt data not found')
        }
        // console.log('about to start SaleReceiptPDF')
        // console.log(SaleReceiptPDF)
        SaleReceiptPDF(data.data)

        LoadingPopup.close()
      } catch (e) {
        console.error(e)
        LoadingPopup.close()
        ErrorPopup.fire('Failed to generate receipt PDF.')
      }
    },
    editStock(stockData) {
      this.$emit('editStock', stockData)
    },
    editSaleInventory(item) {
      this.$emit('edit', item)
    },

    presetRange(preset) {
      const today = new Date()
      const fmt = (d) => {
        const y = d.getFullYear()
        const m = String(d.getMonth() + 1).padStart(2, '0')
        const da = String(d.getDate()).padStart(2, '0')
        return `${y}-${m}-${da}`
      }

      if (preset === 'clear') {
        this.dateStart = null
        this.dateEnd = null
        return
      }
      if (preset === 'today') {
        const d = fmt(today)
        this.dateStart = d
        this.dateEnd = d
        return
      }
      const days = preset === '7d' ? 7 : 30
      const start = new Date(today)
      start.setDate(today.getDate() - (days - 1))
      this.dateStart = fmt(start)
      this.dateEnd = fmt(today)
    },

    async getSaleInv() {
      try {
        const { storeId } = this.$store.state.settings
        const params = {
          index: (this.page - 1) * this.itemsPerPage,
          limit: this.itemsPerPage,
          ...this.filters(),
          storeId,
          consumerId: this.defaultConsumer ? this.defaultConsumer.id : null,
        }
        this.saleInvLoading = true
        const {
          data: { data: items, count },
        } = await saleInvRepository.receiptsV2(params)
        this.saleInvList = items
        this.saleInvCount = count
      } catch (e) {
        if (process.env.NODE_ENV !== 'production') console.error(e)
        this.saleInvErrors = errorHandler(e)
        ErrorPopup.fire('Opps something went wrong')
      } finally {
        this.saleInvLoading = false
      }
    },

    getData() {
      this.saleInvLoading = true
      this.saleInvCounting = true
      searchTimeout(() => {
        this.getSaleInv()
      })
    },
    filters() {
      return {
        search: this.search,
        dateStart: this.dateStart,
        dateEnd: this.dateEnd,
      }
    },

    async voidItem(receiptNo) {
      try {
        if (!this.authPermissions.has('sales:void')) return
        const { isConfirmed } = await ConfirmPopup.fire(
          `Confirm Void #${receiptNo}`,
        )
        if (!isConfirmed) return
        LoadingPopup.fire('Removing Item From Database')
        await appAxios(this.token).post('/v2/sale-inventories/void', {
          receipt_no: receiptNo,
        })
        SuccessPopup.fire(`Receipt ${receiptNo} successfully void`)
        this.getData()
      } catch (e) {
        console.error(e)
        ErrorPopup.fire()
      }
    },
    async onReturnProduct(submitFn) {
      const dialog = this.$refs.returnDialog
      const result = await dialog.open()
      if (!result) return
      await submitFn(result)
      this.getData()
    },

    async printReceipts() {
      try {
        LoadingPopup.fire('Generating PDF...')

        // Use same filters as the table
        const params = this.filters()

        // Hit your print-receipts backend
        const { data } = await appAxios(this.token).get(
          '/sale-inventories/v2/print-receipts',
          { params },
        )
        if (!data.headerData) throw new Error('data.headerData is undefined')
        if (!data.items) throw new Error('data.items is undefined')
        if (!data.items.length) {
          ErrorPopup.fire('No receipts found for the selected filters.')
          return
        }

        const pdf = new PrintReceiptsV2()
        pdf.generate(data.headerData, data.items)
        LoadingPopup.close()
      } catch (e) {
        if (process.env.NODE_ENV !== 'production') console.error(e)
        console.log(e)
        ErrorPopup.fire('Failed to generate PDF.')
      }
    },
  },
  mixins: [moneyMixin],
}
</script>
